import ArrowBackIcon from "@mui/icons-material/ArrowBackIosNew";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  IconButton,
  Tab,
  Tabs,
  Typography,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { formatDistanceToNow } from "date-fns";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import apiService from "../../../../service/Api";
import { tokens } from "../../../../theme";
import { IColorTokens } from "../../../../types/theme";
import { CATEGORY_OPTIONS } from "../types/email-templates";
import { usePermissions } from "../../../../hooks/usePermissions";
import { PERMISSIONS } from "../../../../constants/permissions";

const detailTabs = ["Email Content", "Variables", "Automation"];

interface ApiEmailTemplate {
  id: number;
  name: string;
  description: string;
  category: string;
  email_type: string;
  trigger: string;
  status: "draft" | "published";
  version: string;
  subject: string;
  heading: string;
  message: string;
  button_label: string;
  button_link: string;
  logo_url: string | null;
  banner_url: string | null;
  footer_message: string;
  contact_email: string;
  contact_phone: string;
  design: string;
  sent_count: number;
  updated_at: string;
}

interface ApiEmailVariable {
  id: number;
  name: string;
  description: string;
}

interface ApiEmailTrigger {
  name: string;
  description: string;
}

export default function EmailTemplateDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [activeTab, setActiveTab] = useState(0);
  const [template, setTemplate] = useState<ApiEmailTemplate | null>(null);
  const [loading, setLoading] = useState(true);
  const [statusBusy, setStatusBusy] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [triggerInfo, setTriggerInfo] = useState<ApiEmailTrigger | null>(null);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    apiService
      .get<ApiEmailTemplate>(`/api/configurations/email-templates/${id}/`)
      .then((res) => setTemplate(res.data))
      .catch(() => setTemplate(null))
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    if (!template?.trigger) {
      setTriggerInfo(null);
      return;
    }
    apiService
      .get<{ results: ApiEmailTrigger[] } | ApiEmailTrigger[]>(
        "/api/configurations/email-triggers/?page_size=100",
      )
      .then((res) => {
        const list = Array.isArray(res.data) ? res.data : res.data.results ?? [];
        setTriggerInfo(list.find((t) => t.name === template.trigger) ?? null);
      })
      .catch(() => setTriggerInfo(null));
  }, [template?.trigger]);

  const isPublished = template?.status === "published";

  const togglePublish = async () => {
    if (!template) return;
    setStatusBusy(true);
    try {
      const action = isPublished ? "draft" : "publish";
      await apiService.post(`/api/configurations/email-templates/${template.id}/${action}/`);
      setTemplate((prev) => (prev ? { ...prev, status: isPublished ? "draft" : "published" } : prev));
      toast.success(isPublished ? "Template moved to draft." : "Template published.");
    } catch {
      toast.error("Failed to update template status.");
    } finally {
      setStatusBusy(false);
    }
  };

  const handleDelete = async () => {
    if (!template) return;
    if (!window.confirm(`Delete "${template.name}"? This cannot be undone.`)) return;
    try {
      await apiService.delete(`/api/configurations/email-templates/${template.id}/`);
      toast.success("Template deleted.");
      navigate("/configurations/email-templates");
    } catch {
      toast.error("Failed to delete template.");
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!template) {
    return (
      <Box sx={{ p: 4, textAlign: "center" }}>
        <Typography sx={{ color: colors.primary[300] }}>
          Template not found.
        </Typography>
      </Box>
    );
  }

  const categoryLabel =
    CATEGORY_OPTIONS.find((c) => c.value === template.category)?.label ??
    template.category;

  return (
    <Box sx={{ p: 4, bgcolor: colors.primary[800], minHeight: "100vh" }}>
      <Box sx={{ mb: 2 }}>
        <Button
          startIcon={<ArrowBackIcon sx={{ fontSize: 8 }} />}
          onClick={() => navigate(-1)}
          sx={{
            textTransform: "none",
            color: colors.primary[700],
            bgcolor: colors.primary[900],
            borderRadius: "999px",
            px: 2.5,
            py: 0.8,
            fontSize: "0.85rem",
            fontWeight: 500,
          }}
        >
          Back
        </Button>
      </Box>

      <Box sx={{ display: "flex", gap: 3 }}>
        {/* LEFT COLUMN */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box
            sx={{
              bgcolor: colors.primary[900],
              borderRadius: "20px",
              p: 3,
              mb: 3,
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
              }}
            >
              <Box>
                <Box sx={{ display: "flex", gap: 1, mb: 1.5 }}>
                  <Chip
                    label={categoryLabel}
                    size="small"
                    sx={{
                      bgcolor: colors.primary[800],
                      color: colors.primary[300],
                      height: 22,
                    }}
                  />
                  <Chip
                    label={isPublished ? "Published" : "Draft"}
                    size="small"
                    sx={{
                      bgcolor: isPublished ? "#EAFCF4" : "#FFF0F0",
                      color: isPublished ? "#25C483" : "#FF6B6B",
                      height: 22,
                    }}
                  />
                </Box>
                <Typography
                  variant="h5"
                  sx={{ fontWeight: 700, color: colors.primary[700] }}
                >
                  {template.name}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: colors.primary[300], mt: 0.5 }}
                >
                  {`Last updated ${formatDistanceToNow(new Date(template.updated_at), { addSuffix: true })}`}
                </Typography>
              </Box>

              <Box sx={{ display: "flex", gap: 1 }}>
                {hasPermission(PERMISSIONS.CONFIGURATION_CHANGE) && (
                  <>
                    <Button
                      variant="outlined"
                      onClick={togglePublish}
                      disabled={statusBusy}
                      sx={{
                        textTransform: "none",
                        borderRadius: "10px",
                        color: isPublished ? "#3B82F6" : "#25C483",
                        borderColor: isPublished ? "#3B82F6" : "#25C483",
                      }}
                    >
                      {isPublished ? "Unpublish" : "Publish"}
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={() =>
                        navigate(
                          `/configurations/email-templates/edit/${template.id}`,
                        )
                      }
                      sx={{
                        textTransform: "none",
                        borderRadius: "10px",
                        color: colors.primary[700],
                        borderColor: colors.primary[300] + "50",
                      }}
                    >
                      Edit
                    </Button>
                  </>
                )}
                {hasPermission(PERMISSIONS.CONFIGURATION_DELETE) && (
                  <Button
                    variant="outlined"
                    onClick={handleDelete}
                    sx={{
                      textTransform: "none",
                      borderRadius: "10px",
                      color: "#FF6B6B",
                      borderColor: "#FF6B6B50",
                    }}
                  >
                    Delete
                  </Button>
                )}
              </Box>
            </Box>
          </Box>

          <Box
            sx={{ bgcolor: colors.primary[900], borderRadius: "20px", p: 3 }}
          >
            <Tabs
              value={activeTab}
              onChange={(_, v) => setActiveTab(v)}
              sx={{
                mb: 3,
                "& .MuiTabs-indicator": { bgcolor: colors.primary[400] },
                "& .Mui-selected": {
                  color: `${colors.primary[400]} !important`,
                  fontWeight: 600,
                },
              }}
            >
              {detailTabs.map((t) => (
                <Tab
                  key={t}
                  label={t}
                  sx={{ textTransform: "none", color: colors.primary[300] }}
                />
              ))}
            </Tabs>

            {activeTab === 0 && (
              <EmailContentTab template={template} colors={colors} />
            )}
            {activeTab === 1 && <VariablesTab colors={colors} />}
            {activeTab === 2 && (
              <AutomationTab
                trigger={template.trigger}
                triggerInfo={triggerInfo}
                colors={colors}
              />
            )}
          </Box>
        </Box>

        {/* RIGHT COLUMN */}
        <Box sx={{ width: 300, flexShrink: 0 }}>
          <Box
            sx={{
              bgcolor: colors.primary[900],
              borderRadius: "20px",
              p: 2,
              mb: 3,
            }}
          >
            <Box
              sx={{
                height: 90,
                borderRadius: "14px",
                mb: 2,
                background: `linear-gradient(90deg, ${colors.primary[400]} 0%, ${colors.primary[500]} 50%, ${colors.primary[600]} 100%)`,
              }}
            />
            <Button
              fullWidth
              variant="outlined"
              onClick={() => setPreviewOpen(true)}
              sx={{
                textTransform: "none",
                borderRadius: "10px",
                color: colors.primary[700],
                borderColor: colors.primary[300] + "50",
              }}
            >
              Open Preview
            </Button>
          </Box>

          <Box
            sx={{
              bgcolor: colors.primary[900],
              borderRadius: "20px",
              p: 2.5,
            }}
          >
            <Typography
              sx={{ fontWeight: 600, color: colors.primary[700], mb: 2 }}
            >
              Analytics
            </Typography>
            <Box
              sx={{ bgcolor: colors.primary[800], borderRadius: "12px", p: 1.5 }}
            >
              <Typography
                sx={{
                  fontWeight: 900,
                  fontSize: "1.1rem",
                  color: colors.primary[700],
                }}
              >
                {template.sent_count}
              </Typography>
              <Typography variant="caption" sx={{ color: colors.primary[300] }}>
                Sent
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>

      <Dialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            p: 2,
            borderBottom: "1px solid #F1F5F9",
          }}
        >
          <Typography sx={{ fontWeight: 600 }}>Email Preview</Typography>
          <IconButton size="small" onClick={() => setPreviewOpen(false)}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
        <Box
          sx={{ bgcolor: "#F9FAFB" }}
          dangerouslySetInnerHTML={{ __html: buildPreviewHtml(template) }}
        />
      </Dialog>
    </Box>
  );
}

const buildPreviewHtml = (t: ApiEmailTemplate): string => {
  const heading = t.heading || t.name;
  const message = (t.message || "").replace(/\n/g, "<br/>");
  const showButton = Boolean(t.button_label);

  return `
    <div style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; background:#F9FAFB; padding:24px;">
      <div style="max-width:480px; margin:0 auto; background:#fff; border-radius:16px; overflow:hidden; box-shadow:0 4px 6px -1px rgba(0,0,0,0.06);">
        ${t.banner_url ? `<img src="${t.banner_url}" style="width:100%; display:block;" />` : ""}
        <div style="padding:24px;">
          ${t.logo_url ? `<img src="${t.logo_url}" style="height:32px; margin-bottom:16px;" />` : ""}
          <h2 style="margin:0 0 12px; font-size:18px; color:#000;">${heading}</h2>
          <p style="margin:0 0 20px; font-size:14px; color:#334155; line-height:1.6;">${message}</p>
          ${
            showButton
              ? `<a href="${t.button_link || "#"}" style="display:inline-block; background:#614438; color:#fff; font-size:14px; font-weight:600; text-decoration:none; padding:10px 24px; border-radius:8px;">${t.button_label}</a>`
              : ""
          }
        </div>
        <div style="padding:16px 24px; background:#F8FAFC; border-top:1px solid #F1F5F9;">
          <p style="margin:0; font-size:11px; color:#94A3B8;">${t.footer_message || ""}</p>
        </div>
      </div>
    </div>
  `;
};

function EmailContentTab({
  template,
  colors,
}: {
  template: ApiEmailTemplate;
  colors: IColorTokens;
}) {
  const fields = [
    {
      label: "Subject line",
      value: template.subject,
      meta: `Trigger: ${template.trigger.replace(/_/g, " ")}`,
    },
    { label: "Heading", value: template.heading },
    { label: "Message", value: template.message },
    {
      label: "Button",
      value: template.button_label,
      meta: template.button_link,
    },
  ];

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {fields.map((f) => (
        <Box
          key={f.label}
          sx={{
            bgcolor: colors.primary[800],
            borderRadius: "14px",
            p: 2.5,
          }}
        >
          <Typography
            sx={{
              fontWeight: 600,
              color: colors.primary[700],
              mb: 0.5,
              fontSize: "0.85rem",
            }}
          >
            {f.label}
          </Typography>
          <Typography
            sx={{
              color: colors.primary[700],
              whiteSpace: "pre-line",
              fontSize: "0.9rem",
            }}
          >
            {f.value || "—"}
          </Typography>
          {f.meta && (
            <Typography
              variant="caption"
              sx={{ color: colors.primary[300], display: "block", mt: 1 }}
            >
              {f.meta}
            </Typography>
          )}
        </Box>
      ))}
    </Box>
  );
}

function VariablesTab({ colors }: { colors: IColorTokens }) {
  const [variables, setVariables] = useState<ApiEmailVariable[]>([]);

  useEffect(() => {
    apiService
      .get<{ results: ApiEmailVariable[] } | ApiEmailVariable[]>(
        "/api/configurations/email-variables/?page_size=100",
      )
      .then((res) =>
        setVariables(Array.isArray(res.data) ? res.data : res.data.results ?? []),
      )
      .catch(() => setVariables([]));
  }, []);

  if (!variables.length) {
    return (
      <Typography sx={{ color: colors.primary[300] }}>
        No variables available.
      </Typography>
    );
  }

  return (
    <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
      {variables.map((v) => (
        <Box
          key={v.id}
          sx={{ bgcolor: colors.primary[800], borderRadius: "14px", p: 2 }}
        >
          <Typography
            sx={{ color: "#C29363", fontWeight: 600, fontSize: "0.85rem" }}
          >
            {`{{${v.name}}}`}
          </Typography>
          <Typography variant="caption" sx={{ color: colors.primary[300] }}>
            {v.description}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

function AutomationTab({
  trigger,
  triggerInfo,
  colors,
}: {
  trigger: string;
  triggerInfo: ApiEmailTrigger | null;
  colors: IColorTokens;
}) {
  return (
    <Box>
      <Typography sx={{ fontWeight: 600, color: colors.primary[700], mb: 2 }}>
        Trigger
      </Typography>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          bgcolor: colors.primary[800],
          borderRadius: "14px",
          p: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: "50%",
              bgcolor: "#FDF0E1",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 14,
            }}
          >
            ⚡
          </Box>
          <Box>
            <Typography
              sx={{
                fontWeight: 600,
                fontSize: "0.9rem",
                color: colors.primary[700],
              }}
            >
              {trigger.replace(/_/g, " ")}
            </Typography>
            {triggerInfo?.description && (
              <Typography
                variant="caption"
                sx={{ color: colors.primary[300] }}
              >
                {triggerInfo.description}
              </Typography>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
